/** @jsx jsx */
import { jsx } from 'theme-ui'
import { pokerChipValues, PokerChipValue, PokerChip, UnassignedPokerChip } from './PokerChip'
import React, { useMemo } from 'react'
import { Flex, Box, Txt } from '.'
import _ from 'lodash'
import { FormattedCurrency } from './FormattedCurrency'
import { Positioned, AnimatedPositioned } from './Positioned'
import {
  IPositionCoords,
  IPositionAnimationCoords,
  IPositioningMeasurements,
} from '../page/GameDetails/positionProps'
import { theme } from '../gatsby-plugin-theme-ui/theme'

const pokerChipValuesReverse = [...pokerChipValues].reverse()

type ChipStacks = Array<{ value: PokerChipValue; count: number }>

const calculateStacks = (amount: number) => {
  let amountLeft = amount
  let pokerChipValuesIndex = 0
  const pokerChips: ChipStacks = []
  while (amountLeft) {
    const chipValue = pokerChipValuesReverse[pokerChipValuesIndex]
    if (amountLeft >= chipValue) {
      const count = Math.floor(amountLeft / chipValue)
      pokerChips.push({ count, value: chipValue })
      amountLeft -= count * chipValue
    }
    pokerChipValuesIndex++
  }
  return pokerChips
}

interface UseStacksInfo {
  stacks: ChipStacks
  mostChipsInStack: number
  width: number
  height: number
  stackHeight: number
}

const useStacks = (amount: number): UseStacksInfo => {
  return useMemo(() => {
    const stacks = calculateStacks(amount)
    const mostChipsStack = _.maxBy(stacks, (stack) => stack.count)
    const mostChipsInStack = mostChipsStack?.count || 0
    stacks.reverse()
    const rows = 1
    const stackHeight = chipHeight + mostChipsInStack * chipsOffset
    const height = stackHeight * rows
    const width =
      chipHeight * Math.ceil(stacks.length / rows) + (stacks.length - 1) * theme.space[2]
    return { stacks, mostChipsInStack, width, height, stackHeight }
  }, [amount])
}
const chipHeight = 30
const chipsOffset = 3

export const BasePokerChipStacks: React.SFC<UseStacksInfo> = ({
  stacks,
  mostChipsInStack,
  width,
  height,
  stackHeight,
  ...props
}) => {
  return (
    <Box
      mx="auto"
      {...props}
      css={{
        width,
        height,
        lineHeight: 1,
      }}
    >
      {stacks.map(({ value, count }, stackIndex) => (
        <Box
          key={stackIndex}
          ml={stackIndex ? 2 : 0}
          css={{
            position: 'relative',
            display: 'inline-block',
            height: stackHeight,
            width: chipHeight,
          }}
        >
          {_.times(count).map((_, chipIndex) => (
            <PokerChip
              value={value}
              key={chipIndex}
              height={chipHeight}
              css={{
                position: 'absolute',
                top: (mostChipsInStack - chipIndex - 0) * chipsOffset,
              }}
            />
          ))}
        </Box>
      ))}
    </Box>
  )
}

const Sum: React.SFC<{ amount: number }> = ({ amount, ...props }) => {
  return (
    <Txt
      sx={{ textAlign: 'center', color: 'white', fontSize: 1 }}
      css={{
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        textRendering: 'optimizeLegibility',
        textShadow: `0px 0px 6px rgba(255,255,255,.2)`,
      }}
      {...props}
    >
      <FormattedCurrency amount={amount} />
    </Txt>
  )
}

export interface IPokerChipStacksProps {
  amount: number
  without?: 'stacks' | 'sum'
}

const BasePokerChipStacksAndText: React.SFC<
  IPokerChipStacksProps & { stacksInfo: UseStacksInfo }
> = ({ amount, without, stacksInfo, ...props }) => {
  switch (without) {
    case 'sum':
      return <BasePokerChipStacks {...stacksInfo} {...props} />
    case 'stacks':
      return (
        <UnassignedPokerChip
          backgroundColor="orange"
          formattedNumber={<FormattedCurrency amount={amount} />}
          height={35}
          sx={{ fontSize: 5 }}
        />
      )
    default:
      return (
        <React.Fragment>
          <BasePokerChipStacks {...stacksInfo} {...props} />
          <Sum amount={amount} />
        </React.Fragment>
      )
  }
}

export const PokerChipStacks: React.SFC<IPokerChipStacksProps> = ({ amount, ...props }) => {
  const stacksInfo = useStacks(amount)
  return <BasePokerChipStacksAndText {...{ ...props, stacksInfo, amount }} />
}

export const PositionedPokerChipStacks: React.SFC<
  IPokerChipStacksProps & { coords: IPositionAnimationCoords; style?: React.CSSProperties }
> = ({ coords, amount, without, ...props }) => {
  const stacksInfo = useStacks(amount)
  const height =
    without === 'stacks' ? 40 : without === 'sum' ? stacksInfo.height : stacksInfo.height + 21
  const width = without === 'stacks' ? 40 : stacksInfo.width
  return (
    <AnimatedPositioned {...{ height, width, coords, ...props }}>
      <BasePokerChipStacksAndText {...{ stacksInfo, amount, without }} />
    </AnimatedPositioned>
  )
}
